import { ethers } from 'ethers';

// Contract ABI - ShadeMarket (ZAMA FHE: price PUBLIC, quantity ENCRYPTED)
export const SHADEMARKET_ABI = [
    // Events
    "event AuctionCreated(uint256 indexed auctionId, address indexed seller, address tokenAddress, string title, uint256 totalSupply, uint256 minPriceWei, uint256 startTime, uint256 endTime)",
    "event BidPlaced(uint256 indexed auctionId, uint256 indexed bidId, address indexed bidder, uint256 priceWei, uint256 depositedWei, uint256 timestamp)",
    "event SettlementRequested(uint256 indexed auctionId, bytes32[] encryptedHandles, uint256 timestamp)",
    "event AuctionSettled(uint256 indexed auctionId, uint256 clearingPriceWei, uint256 filledSupply, uint256 totalBids, uint256 timestamp)",
    "event TokensClaimed(uint256 indexed auctionId, uint256 indexed bidId, address indexed bidder, uint256 tokensReceived, uint256 refundAmount)",
    "event AuctionCancelled(uint256 indexed auctionId, uint256 timestamp)",
    "event ProceedsWithdrawn(uint256 indexed auctionId, address indexed seller, uint256 proceeds, uint256 platformFee, uint256 unsoldTokens)",
    "event WatchlistUpdated(address indexed user, uint256 indexed auctionId, bool added)",
    "event TieBreakApplied(uint256 indexed auctionId, uint256 numTiedBids)",
    "event ComparisonPerformed(address indexed user, uint256 indexed bidId1, uint256 indexed bidId2, bytes32 resultKey)",
    "event ReservePriceSet(uint256 indexed auctionId)",
    "event AntiSnipeTriggered(uint256 indexed auctionId, uint256 newEndTime)",
    "event OutbidStatusUpdated(uint256 indexed auctionId, address indexed bidder)",
    "event AutobidSet(uint256 indexed auctionId, address indexed bidder)",
    "event AutobidTriggered(uint256 indexed auctionId, address indexed bidder, uint256 newBidId)",
    "event BulkDiscountSet(uint256 indexed auctionId)",
    "event BulkDiscountApplied(uint256 indexed auctionId, uint256 indexed bidId, uint256 discountBps)",
    "event ActivityExtensionTriggered(uint256 indexed auctionId, uint256 newEndTime)",

    // Write functions
    "function createAuction(address _tokenAddress, string memory _tokenSymbol, string memory _title, string memory _description, uint256 _totalSupply, uint256 _minPriceWei, uint256 _durationSeconds) external returns (uint256)",
    "function placeBid(uint256 _auctionId, uint256 _priceWei, bytes32 _encryptedQuantity, bytes calldata _inputProof) external payable returns (uint256)",
    "function requestSettlement(uint256 _auctionId) external returns (bytes32[])",
    "function finalizeSettlement(uint256 _auctionId, bytes calldata _rawCleartexts, bytes calldata _decryptionProof, uint64[] calldata _orderedQuantities) external",
    "function claimTokens(uint256 _bidId) external",
    "function withdrawProceeds(uint256 _auctionId) external",
    "function cancelAuction(uint256 _auctionId) external",

    // Advanced FHE Functions
    "function setReservePrice(uint256 _auctionId, bytes32 _encryptedReserve, bytes calldata _inputProof) external",
    "function setAntiSnipe(uint256 _auctionId, uint256 _extensionMinutes, bytes32 _encryptedThresholdSeconds, bytes calldata _inputProof) external",
    "function setAutobid(uint256 _auctionId, bytes32 _encryptedMaxBid, bytes calldata _inputProof) external",
    "function checkIfOutbid(uint256 _auctionId) external view returns (bytes32)",
    "function getMyWinProbability(uint256 _auctionId) external view returns (bytes32)",
    "function setBulkDiscount(uint256 _auctionId, bytes32 _tier1Threshold, bytes32 _tier1DiscountBps, bytes32 _tier2Threshold, bytes32 _tier2DiscountBps, bytes calldata _inputProof) external",
    "function setActivityThreshold(uint256 _auctionId, bytes32 _encryptedThreshold, bytes calldata _inputProof) external",
    "function hasReservePrice(uint256 _auctionId) external view returns (bool)",
    "function userHasAutobid(uint256 _auctionId, address _user) external view returns (bool)",
    "function auctionHasReserve(uint256) external view returns (bool)",
    "function auctionAntiSnipeMinutes(uint256) external view returns (uint256)",
    "function auctionHasBulkDiscount(uint256) external view returns (bool)",
    "function hasAutobid(uint256, address) external view returns (bool)",

    // Read functions
    "function getAuction(uint256 _auctionId) external view returns (tuple(address seller, address tokenAddress, string tokenSymbol, string title, string description, uint256 totalSupply, uint256 minPriceWei, uint256 startTime, uint256 endTime, uint256 clearingPriceWei, uint256 totalBidCount, uint256 filledSupply, uint256 totalDeposited, uint8 status))",
    "function getBid(uint256 _bidId) external view returns (address bidder, uint256 auctionId, uint256 priceWei, uint256 depositedWei, uint256 revealedQuantity, uint256 filledQuantity, uint256 refundAmount, bool claimed)",
    "function getEncryptedQuantity(uint256 _bidId) external view returns (bytes32)",
    "function getAuctionBidIds(uint256 _auctionId) external view returns (uint256[])",
    "function getUserBidIds(address _user) external view returns (uint256[])",
    "function getAuctionStatusString(uint256 _auctionId) external view returns (string)",
    "function getTimeRemaining(uint256 _auctionId) external view returns (uint256)",
    "function getTotalAuctions() external view returns (uint256)",
    "function nextAuctionId() external view returns (uint256)",
    "function hasBid(uint256, address) external view returns (bool)",
    "function userAuctionBid(uint256, address) external view returns (uint256)",
    "function sellerWithdrawn(uint256) external view returns (bool)",
    "function platformTreasury() external view returns (address)",
    "function totalFeesCollected() external view returns (uint256)",
    "function PLATFORM_FEE_BPS() external view returns (uint256)",

    // FHE features
    "function getEncryptedTotalDemand(uint256 _auctionId) external view returns (bytes32)",
    "function addToWatchlist(uint256 _auctionId) external",
    "function removeFromWatchlist(uint256 _auctionId) external",
    "function isWatching(uint256 _auctionId) external view returns (bool)",
    "function getMyWatchlistCount() external view returns (bytes32)",
    "function compareMyBidQuantities(uint256 _bidId1, uint256 _bidId2) external returns (bytes32)",
    "function isBidQuantityAbove(uint256 _bidId, bytes32 _threshold, bytes calldata _inputProof) external returns (bytes32)",
    "function generateEncryptedRandom() external returns (bytes32)",
    "function getComparisonResult(bytes32 _resultKey) external view returns (bytes32)",

    // Production features
    "function createAuctionFromTemplate(address _tokenAddress, string _tokenSymbol, string _title, uint256 _totalSupply, uint256 _minPriceWei, uint8 _template) external returns (uint256)",
    "function getPlatformAnalytics() external view returns (uint256 totalAuctions, uint256 settledAuctions, uint256 volumeTraded, uint256 feesCollected)",
    "function getUserAnalytics(address _user) external view returns (uint256 totalBids, uint256 auctionsParticipated)",
    "function getAuctionAnalytics(uint256 _auctionId) external view returns (uint256 bidCount, uint256 totalDeposited, uint256 filledSupply, uint256 clearingPrice, bool isSettled)",
    "function batchClaimTokens(uint256[] calldata _bidIds) external",
    "function batchIsWatching(uint256[] calldata _auctionIds) external view returns (bool[])"
];

// ERC20 ABI (simplified + mint for faucet)
export const ERC20_ABI = [
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function decimals() view returns (uint8)",
    "function balanceOf(address) view returns (uint256)",
    "function allowance(address owner, address spender) view returns (uint256)",
    "function approve(address spender, uint256 amount) returns (bool)",
    "function transfer(address to, uint256 amount) returns (bool)",
    "function mint(address to, uint256 amount) external"
];

// Contract addresses from environment
export const SHADEMARKET_ADDRESS = import.meta.env.VITE_SHADEMARKET_ADDRESS || import.meta.env.VITE_SILENTBID_ADDRESS || '0x0000000000000000000000000000000000000000';
export const MOCK_TOKEN_ADDRESS = import.meta.env.VITE_MOCK_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000';

// Legacy export for backwards compatibility
export const SILENTBID_ADDRESS = SHADEMARKET_ADDRESS;

/**
 * Check if contract is deployed (not placeholder address)
 */
export function isContractDeployed() {
    return SHADEMARKET_ADDRESS !== '0x0000000000000000000000000000000000000000';
}

/**
 * Get ShadeMarket contract instance
 */
export function getShadeMarketContract(signerOrProvider) {
    return new ethers.Contract(SHADEMARKET_ADDRESS, SHADEMARKET_ABI, signerOrProvider);
}

// Legacy export for backwards compatibility
export const getSilentBidContract = getShadeMarketContract;

/**
 * Get ERC20 contract instance
 */
export function getERC20Contract(tokenAddress, signerOrProvider) {
    return new ethers.Contract(tokenAddress, ERC20_ABI, signerOrProvider);
}

/**
 * Fetch all auctions
 */
export async function fetchAuctions(contract) {
    if (!isContractDeployed()) return [];

    try {
        const totalAuctions = await contract.nextAuctionId();
        const auctions = [];

        for (let i = 1; i < totalAuctions; i++) {
            try {
                const auction = await contract.getAuction(i);
                const status = await contract.getAuctionStatusString(i);
                const timeRemaining = await contract.getTimeRemaining(i);

                auctions.push({
                    id: i,
                    seller: auction.seller,
                    tokenAddress: auction.tokenAddress,
                    tokenSymbol: auction.tokenSymbol,
                    title: auction.title,
                    description: auction.description,
                    totalSupply: auction.totalSupply,
                    minPriceWei: auction.minPriceWei,
                    startTime: Number(auction.startTime) * 1000,
                    endTime: Number(auction.endTime) * 1000,
                    clearingPriceWei: auction.clearingPriceWei,
                    totalBidCount: Number(auction.totalBidCount),
                    filledSupply: auction.filledSupply,
                    totalDeposited: auction.totalDeposited,
                    status: status,
                    timeRemaining: Number(timeRemaining)
                });
            } catch (e) {
                console.error(`Error fetching auction ${i}:`, e);
            }
        }

        return auctions;
    } catch (error) {
        console.error('Failed to fetch auctions:', error);
        return [];
    }
}

/**
 * Fetch user's bids
 */
export async function fetchUserBids(contract, userAddress) {
    if (!isContractDeployed()) return [];

    try {
        const bidIds = await contract.getUserBidIds(userAddress);
        const bids = [];

        for (const bidId of bidIds) {
            try {
                const bid = await contract.getBid(bidId);
                const auction = await contract.getAuction(bid.auctionId);
                
                const STATUS_MAP = ['Created', 'Active', 'Ended', 'Settled', 'Cancelled'];
                let auctionStatus = STATUS_MAP[Number(auction.status)] || 'Unknown';
                
                const now = Math.floor(Date.now() / 1000);
                if (auctionStatus === 'Active' && Number(auction.endTime) < now) {
                    auctionStatus = 'Ended';
                }

                bids.push({
                    id: Number(bidId),
                    bidder: bid.bidder,
                    auctionId: Number(bid.auctionId),
                    auctionTitle: auction.title,
                    tokenSymbol: auction.tokenSymbol,
                    auctionStatus,
                    auctionClearingPrice: auction.clearingPriceWei,
                    priceWei: bid.priceWei,
                    depositedWei: bid.depositedWei,
                    revealedQuantity: bid.revealedQuantity,
                    filledQuantity: bid.filledQuantity,
                    refundAmount: bid.refundAmount,
                    claimed: bid.claimed
                });
            } catch (e) {
                console.error(`Error fetching bid ${bidId}:`, e);
            }
        }

        return bids;
    } catch (error) {
        console.error('Failed to fetch user bids:', error);
        return [];
    }
}

/**
 * Format wei to ETH string
 */
export function formatEth(wei, decimals = 4) {
    if (!wei) return '0';
    const value = parseFloat(ethers.formatEther(wei));
    if (value > 0 && value < 0.0001) return value.toFixed(8);
    if (value > 0 && value < 0.01) return value.toFixed(6);
    return value.toFixed(decimals);
}

/**
 * Format token amount
 */
export function formatTokenAmount(amount, decimals = 18, displayDecimals = 2) {
    if (!amount) return '0';
    const formatted = ethers.formatUnits(amount, decimals);
    return parseFloat(formatted).toFixed(displayDecimals);
}

/**
 * Parse ETH to wei
 */
export function parseEth(eth) {
    return ethers.parseEther(eth.toString());
}

/**
 * Fetch all bids for an auction (for leaderboard)
 */
export async function fetchAuctionBids(contract, auctionId) {
    if (!isContractDeployed()) return [];

    try {
        const bidIds = await contract.getAuctionBidIds(auctionId);
        const bids = [];

        for (const bidId of bidIds) {
            try {
                const bid = await contract.getBid(bidId);
                bids.push({
                    id: Number(bidId),
                    bidder: bid.bidder,
                    priceWei: bid.priceWei,
                    depositedWei: bid.depositedWei,
                    revealedQuantity: bid.revealedQuantity,
                    filledQuantity: bid.filledQuantity,
                    claimed: bid.claimed
                });
            } catch (e) {
                console.error(`Error fetching bid ${bidId}:`, e);
            }
        }

        bids.sort((a, b) => {
            if (b.priceWei > a.priceWei) return 1;
            if (b.priceWei < a.priceWei) return -1;
            return 0;
        });

        return bids;
    } catch (error) {
        console.error('Failed to fetch auction bids:', error);
        return [];
    }
}
